import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PermissaoDoConector } from '@orbind/core'
import { api } from '../../lib/api.ts'

/**
 * Orbind AI (SPEC-20261104-2229). As conversas ficam no SERVIDOR: a tela manda
 * só a mensagem nova (ou a escolha de uma pergunta) e o id da conversa, e lê o
 * que mostrar. Sair da tela e voltar reabre a conversa como estava.
 */

export type SituacaoDoAssistente = {
  configurado: boolean
  noPlano: boolean
  liberado: boolean
  permissoes: PermissaoDoConector[]
}

/** Pergunta de decisão: opções clicáveis, a recomendada primeiro. */
export type PerguntaDoAssistente = {
  id: string
  pergunta: string
  opcoes: { rotulo: string; descricao: string | null; recomendada: boolean }[]
  motivo: string
  multipla: boolean
}

export type FalaDaConversa = {
  quem: 'pessoa' | 'assistente'
  texto: string
  acoes?: { ferramenta: string; erro: boolean }[]
  pergunta?: PerguntaDoAssistente | null
  respondida?: string
  em: string
}

export type ConversaDoAssistente = {
  id: string
  titulo: string
  falas: FalaDaConversa[]
  pensando: boolean
  atualizadaEm: string
}

export type ResumoDaConversa = { id: string; titulo: string; atualizadaEm: string; pensando: boolean }

export type PedidoAoAssistente = {
  conversa?: string
  mensagem?: string
  resposta?: { pergunta: string; resposta: string }
}

const CHAVE = ['ai-assistant'] as const
const chaveDaLista = [...CHAVE, 'conversas'] as const
export const chaveDaConversa = (id: string) => [...CHAVE, 'conversa', id] as const

async function lerErro(resposta: Response, padrao: string): Promise<never> {
  const corpo = (await resposta.json().catch(() => ({}))) as { error?: { message?: string } }
  throw new Error(corpo.error?.message ?? padrao)
}

export function useSituacaoDoAssistente() {
  return useQuery({
    queryKey: [...CHAVE, 'me'],
    queryFn: async () => {
      const r = await api['ai-assistant'].me.$get()
      if (!r.ok) await lerErro(r, 'Não foi possível carregar o Orbind AI.')
      return (await r.json()) as SituacaoDoAssistente
    },
  })
}

export function useConversas(habilitado: boolean) {
  return useQuery({
    queryKey: chaveDaLista,
    enabled: habilitado,
    queryFn: async () => {
      const r = await api['ai-assistant'].conversas.$get()
      if (!r.ok) await lerErro(r, 'Não foi possível carregar as conversas.')
      return (await r.json()) as ResumoDaConversa[]
    },
  })
}

/** A conversa aberta. Enquanto o Orbind AI responde (a pessoa saiu e voltou), confere de 3 em 3 s. */
export function useConversa(id: string | null) {
  return useQuery({
    queryKey: chaveDaConversa(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const r = await api['ai-assistant'].conversas[':id'].$get({ param: { id: id! } })
      if (!r.ok) await lerErro(r, 'Não foi possível abrir a conversa.')
      return (await r.json()) as ConversaDoAssistente
    },
    refetchInterval: (q) => (q.state.data?.pensando ? 3000 : false),
  })
}

export function useEnviarAoAssistente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: PedidoAoAssistente) => {
      const r = await api['ai-assistant'].chat.$post({ json: pedido })
      if (!r.ok) await lerErro(r, 'O Orbind AI não conseguiu responder agora.')
      return (await r.json()) as ConversaDoAssistente
    },
    onSuccess: (conversa) => {
      qc.setQueryData(chaveDaConversa(conversa.id), conversa)
      void qc.invalidateQueries({ queryKey: chaveDaLista })
    },
  })
}

export function useApagarConversa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api['ai-assistant'].conversas[':id'].$delete({ param: { id } })
      if (!r.ok) await lerErro(r, 'Não foi possível apagar a conversa.')
      return id
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: chaveDaConversa(id) })
      void qc.invalidateQueries({ queryKey: chaveDaLista })
    },
  })
}

/** Nome que a pessoa entende para cada ferramenta, no rodapé da resposta. */
export const ROTULO_DA_FERRAMENTA: Record<string, string> = {
  meus_itens: 'consultou seus Itens',
  buscar_itens: 'buscou Itens',
  ver_item: 'abriu um Item',
  catalogos: 'consultou os catálogos',
  resumo_do_dia: 'montou o resumo do dia',
  evolucao_dos_itens: 'consultou a evolução',
  atualizar_item: 'alterou um Item',
  mover_para_etapa: 'moveu um Item de etapa',
  adicionar_checklist: 'mexeu num checklist',
  comentar_item: 'comentou num Item',
  concluir_item: 'concluiu um Item',
  criar_item: 'criou um Item',
  criar_campo_complementar: 'criou um campo complementar',
}
